/**
 * The table every report draws, and the strip of controls above it.
 *
 * Seven reports each hand-rolled their own `<table>`, which meant seven places
 * to apply the wrap class, seven colgroups to keep aligned with seven header
 * rows, and — the one that actually bit — a column list maintained twice, so
 * the CSV could export the wrong values under the right headings.
 *
 * The controls used to be scattered the same way: wrap was a boolean in one
 * file, the column choice a Set in another, the download eight hand-written
 * shapers in a third, and the flip two complete copies of a `<table>`. They
 * are all one thing — HOW AM I READING THIS — so they are now one strip, in
 * one place, on every table:
 *
 *   density  fit / wrap / long line — how much room a cell may take
 *   columns  which of them are on screen, AND THEREFORE in the file
 *   flip     read the grid the other way round; the picker follows it
 *   CSV      exactly what the picker left on screen, in the order it is in
 *
 * Each is remembered per table, because the answer differs by table: a roster
 * of long ingredient lists and a grid of doses do not want the same one.
 */
import { useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { TableFilterContext, filterWords, matchesFilter } from './tableFilter';
import DownloadCsv from './DownloadCsv';
import TableGrid from './TableGrid';
import { canFlip, rememberFlip, storedFlip, transpose } from './flip';
import { canNumber, rememberNumbers, storedNumbers } from './rowNumbers';
import { csvName, tableCsv } from './download';
import { pickedLabel, rememberHidden, storedHidden, toggle, visible } from './columnChoice';
import {
  DENSITIES,
  DENSITY_LABEL,
  DENSITY_TITLE,
  rememberMode,
  storedMode,
  type Density,
} from './tableLayout';
import type { Column, Sort } from './tableColumns';
import type { SeenMarks } from '../useSeen';

const BTN =
  'px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700';

export interface DataTableProps<T> {
  /** Names the table's memory — its density, its column choice, which way
   *  round it is — and the file it downloads. */
  table: string;
  /** The room the file came out of, so the reader can find it again. */
  room?: string;
  /** What to call the file, where the storage id is not what a reader would
   *  call it — the price map keeps a table per solution type, so its ids are
   *  `price-map-st:12` and its files are `prices-scheduling`. */
  file?: string;
  /** The card's own heading, on the SAME line as the controls. Without this a
   *  caller draws its heading, then its own context — a price line, a legend —
   *  and the controls land in between, floating in the middle of the card
   *  belonging to neither. */
  title?: ReactNode;
  /** Anything that belongs to the table but sits ABOVE the grid rather than
   *  beside the controls: the price line, the legend of what a cell means.
   *  Outside the sideways scroller, because it describes the whole table and
   *  should not slide away from it. */
  beforeTable?: ReactNode;
  /** Anything else that belongs on the strip: a filter checkbox, a unit
   *  select. It sits before the controls every table has. */
  toolbar?: ReactNode;
  columns: Column<T>[];
  rows: T[];
  /** Names the row, and is its test id: the caller keeps whatever contract it
   *  already had rather than inheriting one from here. Flipped, it names the
   *  COLUMN that row becomes. */
  rowKey: (row: T) => string | number;
  /** Where the columns are open-ended — one per product — start at long line,
   *  because dividing a screen sixty ways is not a table. */
  defaultMode?: Density;
  /** And what to start at once flipped, when the two orientations are not the
   *  same shape: thirteen attributes across fit a screen where sixty products
   *  do not. Defaults to the upright one. */
  flippedMode?: Density;
  /** Which way round to read it before anybody has said. The many go down the
   *  side and the few across the top: twelve attributes across a screen fit,
   *  a hundred and nineteen products do not. */
  defaultFlipped?: boolean;
  /** Flip is offered up to this many rows, on an ordinary bounded LIST: past
   *  that a flip would make a column per row and read as nothing. A grid — a
   *  table that declared `defaultMode="long"` — always offers it, being wide
   *  already. 0 never offers it. */
  flipMax?: number;
  /** Rotated headers on the columns the FLIP generates — a grid with a column
   *  per product has no room to write names across. Upright, a column says so
   *  itself with `vertical`, because its summary columns still have room. */
  verticalHeads?: boolean;
  /** A band across the rows that follow, when the value changes. Upright
   *  only: a band across a set of COLUMNS is not a thing. */
  section?: (row: T) => string | null;
  detail?: (row: T) => ReactNode;
  expanded?: (row: T) => boolean;
  /** THE ONE EXCEPTION. The opportunity brief's rows do not flatten — each is
   *  a paragraph, a list of sentences and four lists of its own — so upright
   *  it draws its own cards, and flipping gives the real table: an idea per
   *  column, its fields down the side. The strip is the same strip. */
  unflipped?: ReactNode;
  /** Shown in place of the body when nothing survives the filters. */
  empty?: ReactNode;
  rowClass?: (row: T) => string;
  /** Extra `data-*` on the row. A finding worth marking — a pain nobody
   *  addresses — is a fact about the row, not a colour, and things outside
   *  this component read it. */
  rowData?: (row: T) => Record<string, string>;
  testId?: string;
  /** Ids that changed and have not been looked at. A row carrying one is lit
   *  until it is genuinely on screen, then fades. Upright only — flipped, a
   *  row is a field, and a field does not change. */
  unseen?: ReadonlySet<number>;
  /** Called with ids now on screen — the caller drops them from the badge,
   *  which reads from the same set. */
  onSeen?: (ids: number[]) => void;
  /** THE REPORT'S OWN TRACKER, where the report draws more than one table.
   *  One per report, or each table prunes the others' marks — see the note in
   *  `TableGrid`. */
  seen?: SeenMarks;
  /** The row's item id, when it is not the row key. */
  rowItemId?: (row: T) => number;
  /** The row's ref, where the CALLER does its own unread tracking across
   *  several tables at once. */
  rowRef?: (row: T) => (el: HTMLElement | null) => void;
}

export default function DataTable<T>({
  table,
  room,
  file,
  title,
  toolbar,
  beforeTable,
  columns,
  rows: allRows,
  rowKey,
  defaultMode = 'fit',
  flippedMode,
  defaultFlipped = false,
  flipMax = 30,
  verticalHeads,
  section,
  detail,
  expanded,
  unflipped,
  empty,
  rowClass,
  rowData,
  testId,
  unseen,
  onSeen,
  seen,
  rowItemId,
  rowRef,
}: DataTableProps<T>) {
  const [modes, setModes] = useState<[Density, Density]>(() => [
    storedMode(table, defaultMode, false),
    storedMode(table, flippedMode ?? defaultMode, true),
  ]);
  const [flipped, setFlipped] = useState<boolean>(() => storedFlip(table, defaultFlipped));
  // OFF UNTIL ASKED FOR, then remembered for this table. A roster somebody
  // counts down and a two-row summary do not want the same answer.
  const [numbered, setNumbered] = useState<boolean>(() => storedNumbers(table));
  const setNumbered_ = (next: boolean) => {
    setNumbered(next);
    rememberNumbers(table, next);
  };
  /* A choice per orientation, all the way down: hiding "price" and hiding
     "Vagaro" are not the same act, and neither is sorting by one. */
  const [hidden, setHidden] = useState<[Set<string>, Set<string>]>(() => [
    storedHidden(
      table,
      false,
      columns.filter(c => c.hiddenByDefault).map(c => c.key),
    ),
    storedHidden(table, true),
  ]);
  const [sorts, setSorts] = useState<[Sort | null, Sort | null]>([null, null]);
  // THE PAGE'S TEXT FILTER, applied here so every table narrows together.
  // Against the caller's rows, before any flip: a row is what a reader means
  // by "the ones that mention acme", whichever way round it is drawn.
  const filterText = useContext(TableFilterContext);
  const words = useMemo(() => filterWords(filterText), [filterText]);
  const rows = useMemo(
    () => (words.length ? allRows.filter(r => matchesFilter(r, columns, words)) : allRows),
    [allRows, columns, words],
  );
  const side = flipped ? 1 : 0;
  const mode = modes[side];

  const setMode_ = (next: Density) => {
    setModes(cur => (flipped ? [cur[0], next] : [next, cur[1]]));
    rememberMode(table, next, flipped);
  };
  const setFlipped_ = (next: boolean) => {
    setFlipped(next);
    rememberFlip(table, next);
  };
  const hide = (key: string) => {
    const next = toggle(hidden[side], key);
    setHidden(cur => (flipped ? [cur[0], next] : [next, cur[1]]));
    rememberHidden(table, next, flipped);
  };
  const onSort = (next: Sort | null) =>
    setSorts(cur => (flipped ? [cur[0], next] : [next, cur[1]]));

  /* Upright, the grid is what the caller passed. Flipped, it is the transpose
     — same cells, same accessors, read the other way round. */
  const grid = useMemo(() => {
    if (!flipped) return null;
    const t = transpose(columns, rows, rowKey);
    /* The flip is what makes the columns too many to name across, so it is
       the flip that turns their headers on their side. */
    if (!verticalHeads) return t;
    return { rows: t.rows, columns: t.columns.map((c, i) => (i === 0 ? c : { ...c, vertical: true })) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flipped, columns, rows, verticalHeads]);

  const all: Column<any>[] = grid ? grid.columns : columns;
  const gridRows: any[] = grid ? grid.rows : rows;
  const gridKey = grid ? (f: any) => f.key : (rowKey as (r: any) => string | number);
  const shown = visible(all, hidden[side]);
  const sort = sorts[side];
  /* Flipped, the open rows' detail is drawn under the table rather than inside
     it (see the render). Still the caller's own `expanded`/`detail`, read off
     the UPRIGHT rows — which is what both are written in terms of. */
  const openDetail =
    flipped && detail && expanded
      ? rows.filter(expanded).map(r => (
          <div
            key={`detail-${rowKey(r)}`}
            className="px-2 py-1 border-t border-gray-100 dark:border-gray-700"
            data-testid={testId ? `${testId}-detail` : undefined}
          >
            {detail(r)}
          </div>
        ))
      : null;

  const strip = (
    <Strip
      table={file ?? table}
      room={room}
      title={title}
      extra={toolbar}
      mode={mode}
      onMode={setMode_}
      all={all}
      hidden={hidden[side]}
      onToggle={hide}
      flipped={flipped}
      /* A table that declared itself unbounded is a grid, and a grid always
         offers the turn however many rows it has. */
      onFlip={
        canFlip(rows.length, flipMax, flipped, defaultMode === 'long')
          ? () => setFlipped_(!flipped)
          : undefined
      }
      numbered={numbered}
      onNumbers={
        canNumber(gridRows.length, flipped) ? () => setNumbered_(!numbered) : undefined
      }
      csv={() => tableCsv(shown, gridRows, sort)}
    />
  );

  if (unflipped && !flipped) {
    return (
      <>
        {strip}
        {unflipped}
      </>
    );
  }

  return (
    <>
      {strip}
      {beforeTable}
      {/* SOMETHING ALWAYS HAS TO SCROLL. This was gated on long line, on the
          reasoning that fit and wrap constrain the cells and therefore fit —
          which holds for a table of six columns and not at all for a matrix of
          thirty-one products, whose sideways headers have a floor no density
          setting can squeeze. With no scroller the table simply overflowed its
          card and the first column went off the left edge with no way to bring
          it back. `overflow-x-auto` shows nothing when the content fits, so
          there is no cost to a table that never needed it. The caller keeps
          its own card and its own vertical scroller; this is only the sideways
          one the table itself needs. */}
      <div className="overflow-x-auto" data-testid={testId ? `${testId}-scroll` : undefined}>
        <TableGrid
          columns={shown}
          rows={gridRows}
          rowKey={gridKey}
          mode={mode}
          sort={sort}
          onSort={onSort}
          axis={flipped ? 'flipped' : 'upright'}
          /* Never flipped: the rows are the old columns then, so a number down
             the side counts fields rather than things. */
          numbered={numbered && !flipped}
          section={flipped ? undefined : (section as any)}
          detail={flipped ? undefined : (detail as any)}
          expanded={flipped ? undefined : (expanded as any)}
          empty={
            words.length && allRows.length && !rows.length ? (
              <span data-testid="filter-empty">Nothing in this table matches “{filterText.trim()}”.</span>
            ) : (
              empty
            )
          }
          rowClass={flipped ? undefined : (rowClass as any)}
          rowData={flipped ? undefined : (rowData as any)}
          testId={testId}
          /* The marks ride whichever axis holds real items. Upright that is
             the report's own rows; flipped it is the columns that named a
             product — a spec sheet's rows are ATTRIBUTES, which the room did
             not create and which nothing can be unread about. */
          unseen={unseen}
          onSeen={onSeen}
          seen={seen}
          rowItemId={flipped ? (f: any) => f.itemId ?? NaN : (rowItemId as any)}
          rowRef={flipped ? undefined : (rowRef as any)}
        />
      </div>
      {/* THE DETAIL STILL HAS SOMEWHERE TO GO. A detail row cannot hang under a
          row that is now a column, so the grid is not given one — but the
          transpose keeps the CELL renderers, so a count drawn as a button stays
          a button, and on the landscape map every one of them went on firing
          into a detail nothing was rendering. Clickable, responsive, silent,
          and remembered per table in localStorage, so it read as half the
          numbers on the page being dead at random.
          Under the table is where it fits: the row it belongs to is a column
          overhead, and its own heading names it. */}
      {flipped && openDetail}
    </>
  );
}

/** The strip. Same controls, same order, on every table — so "where is
 *  download" is answered once rather than per report. */
function Strip({
  table,
  room,
  title,
  extra,
  mode,
  onMode,
  all,
  hidden,
  onToggle,
  flipped,
  onFlip,
  numbered,
  onNumbers,
  csv,
}: {
  table: string;
  room?: string;
  title?: ReactNode;
  extra?: ReactNode;
  mode: Density;
  onMode: (next: Density) => void;
  all: Column<any>[];
  hidden: Set<string>;
  onToggle: (key: string) => void;
  flipped: boolean;
  onFlip?: () => void;
  /** Numbering is a reading aid, so it is a toggle rather than a column —
   *  a position is not a value, and the file must not carry one. */
  numbered?: boolean;
  onNumbers?: () => void;
  csv: () => string;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const picked = pickedLabel(all, hidden);
  return (
    <div className="flex items-center gap-2 text-xs mb-1.5" data-testid="table-toolbar">
      {title && <div className="min-w-0 text-gray-600 dark:text-gray-300">{title}</div>}
      <div className="ml-auto flex items-center gap-2">
        {extra}
        {/* ONE AT A TIME, so it is a pick and not three buttons. As a
            segmented control it spent a third of the strip saying two things
            the reader had not chosen — on a report drawing a table per market
            that is the same three words over and over down the page. */}
        <select
          value={mode}
          onChange={e => onMode(e.target.value as Density)}
          aria-label="How much room a cell may take"
          title={DENSITY_TITLE[mode]}
          data-testid="density"
          className="rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800 px-1 py-0.5 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          {DENSITIES.map(d => (
            <option key={d} value={d}>
              {DENSITY_LABEL[d]}
            </option>
          ))}
        </select>
        <div className="relative" ref={box}>
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            aria-haspopup="true"
            data-testid="column-picker"
            className={BTN}
          >
            Columns{picked && <span className="text-gray-400"> · {picked}</span>} ▾
          </button>
          {open && (
            <div
              className="absolute right-0 z-20 mt-1 max-h-72 overflow-y-auto rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg p-2 min-w-[12rem]"
              data-testid="column-menu"
            >
              {all.map(c => (
                <label
                  key={c.key}
                  className={`flex items-center gap-1.5 py-0.5 ${c.always ? 'opacity-50' : 'cursor-pointer'}`}
                  title={c.always ? 'The table is keyed by this one' : undefined}
                >
                  <input
                    type="checkbox"
                    checked={c.always || !hidden.has(c.key)}
                    disabled={c.always}
                    onChange={() => onToggle(c.key)}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="truncate">{c.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        {onNumbers && (
          <button
            type="button"
            onClick={onNumbers}
            aria-pressed={numbered}
            data-testid="row-numbers"
            title={
              numbered
                ? 'Stop numbering the rows'
                : 'Number the rows as they are on screen, for counting and pointing'
            }
            className={BTN}
          >
            #
          </button>
        )}
        {onFlip && (
          <button
            type="button"
            onClick={onFlip}
            aria-pressed={flipped}
            data-testid="flip-axis"
            title={
              flipped
                ? 'Read it back the other way: these columns down the side again'
                : 'Read it the other way round: the rows across the top, the columns down the side'
            }
            className={BTN}
          >
            ⇄ flip
          </button>
        )}
        {/* The file is what the picker left on the screen. */}
        <DownloadCsv csv={csv} filename={csvName(room ?? 'boardroom', table)} label="CSV" />
      </div>
    </div>
  );
}
